import { useEffect, useRef, useState } from 'react';
import type { AchievementItem } from '@/mocks/achievements';
import MediaPreview from '@/components/base/MediaPreview';

interface AchievementShowcaseProps {
  achievement: AchievementItem;
}

// ── Video Player Component ──
function VideoPlayer({ poster, videoUrl }: { poster: string; videoUrl: string }) {
  const [showModal, setShowModal] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <div
        className="relative rounded-lg overflow-hidden h-[320px] md:h-[420px] cursor-pointer group/video"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setShowModal(true)}
      >
        <video
          src={videoUrl}
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover/video:scale-105"
          muted
          playsInline
          disablePictureInPicture
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/15 transition-opacity duration-500 group-hover/video:opacity-80" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all duration-500 ${
              hovered ? 'scale-110' : 'scale-100'
            }`}
            style={{
              background: 'rgba(189,1,1,0.85)',
              boxShadow: hovered ? '0 0 48px rgba(189,1,1,0.55)' : '0 0 28px rgba(189,1,1,0.3)',
            }}
          >
            <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1" />
          </div>
        </div>
        <div className="absolute top-4 left-4 z-10">
          <span className="text-white/80 text-xs bg-black/35 px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
            <i className="ri-video-line" />
            成果视频
          </span>
        </div>
      </div>

      {showModal && (
        <MediaPreview
          type="video"
          url={videoUrl}
          title="成果视频"
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// ── Main Showcase Component ──
export default function AchievementShowcase({ achievement }: AchievementShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !visible) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  const { videoPoster, videoUrl } = achievement.detail.homepage;

  return (
    <section id="achievement-showcase" className="relative pt-14 md:pt-20 pb-4 md:pb-6 px-4 md:px-8 bg-background-50 overflow-hidden">
      {/* ━━━ Impact Shockwave Ring ━━━ */}
      {visible && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div
            className="absolute rounded-full border-2 border-primary-500/40"
            style={{
              width: '60px',
              height: '60px',
              animation: 'shockwaveRing 3.0s ease-out infinite',
            }}
          />
          <div
            className="absolute rounded-full border-2 border-primary-400/25"
            style={{
              width: '60px',
              height: '60px',
              animation: 'shockwaveRing 3.0s ease-out 1.0s infinite',
            }}
          />
          <div
            className="absolute rounded-full border border-accent-400/20"
            style={{
              width: '60px',
              height: '60px',
              animation: 'shockwaveRing 3.0s ease-out 2.0s infinite',
            }}
          />
        </div>
      )}

      {/* ━━━ Sweeping Beam ━━ */}
      {visible && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-0 bottom-0"
            style={{
              width: '80px',
              background: 'linear-gradient(90deg, transparent, rgba(189,1,1,0.06), transparent)',
              animation: 'beamSweep 3.5s ease-out infinite',
            }}
          />
        </div>
      )}

      <div className="max-w-[1200px] mx-auto" ref={sectionRef}>
        {/* ═══════════════════════════════════════
            TWO-COLUMN LAYOUT: 成果简介 | 成果视频
            ═══════════════════════════════════════ */}
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8 mb-8 md:mb-10">
          {/* ━━ LEFT: 成果简介 ━━ */}
          <div
            className="relative flex-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0) scale(1)' : 'translateX(-60px) scale(0.9)',
              filter: visible ? 'blur(0)' : 'blur(3px)',
              transition: 'opacity 1.0s cubic-bezier(0.16,1,0.22,1) 0.15s, transform 1.0s cubic-bezier(0.16,1,0.22,1) 0.15s, filter 1.0s cubic-bezier(0.16,1,0.22,1) 0.15s',
            }}
          >
            {/* ── BLEED: offset accent frame ── */}
            <div className="absolute inset-0 translate-x-[10px] translate-y-[10px] rounded-lg border border-accent-400/40 pointer-events-none hidden md:block" />
            {/* ── BLEED: bold accent rail ── */}
            <div className="absolute left-0 -top-3 -bottom-3 w-[5px] rounded-full bg-accent-500 z-20 pointer-events-none" />

            <div className="relative bg-primary-500 rounded-lg overflow-hidden h-full flex flex-col pl-[5px]">
            {/* Faint white rail texture */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.06]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.05]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />

            <div className="relative z-10 p-6 md:p-8 lg:p-9 flex-1 flex flex-col">
            {/* ── Header: label + number synced style ── */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="block font-heading font-black text-[24px] sm:text-[32px] md:text-[38px] leading-none text-white tracking-tight">
                  成果简介
                </span>
                <span className="hidden sm:block text-[10px] text-white/40 font-medium tracking-[0.18em] uppercase">Achievement Overview</span>
              </div>
              <span className="block font-heading font-black text-[48px] sm:text-[56px] md:text-[72px] lg:text-[88px] leading-none text-white/12 select-none pointer-events-none">
                01
              </span>
            </div>

            {/* ── Summary ── */}
            <div className="relative bg-white/[0.08] rounded-lg border-l-2 border-accent-500 pl-5 pr-5 py-4">
              <div className="text-white/80 text-sm md:text-[15px] leading-[1.8] max-w-xl" dangerouslySetInnerHTML={{ __html: achievement.detail.homepage.summary }} />
            </div>
            </div>
            </div>
          </div>

          {/* ━━ RIGHT: 成果视频 ━━ */}
          <div
            className="relative flex-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0) scale(1)' : 'translateX(60px) scale(0.9)',
              filter: visible ? 'blur(0)' : 'blur(3px)',
              transition: 'opacity 1.0s cubic-bezier(0.16,1,0.22,1) 0.35s, transform 1.0s cubic-bezier(0.16,1,0.22,1) 0.35s, filter 1.0s cubic-bezier(0.16,1,0.22,1) 0.35s',
            }}
          >
            {/* ── BLEED: offset accent frame ── */}
            <div className="absolute inset-0 translate-x-[10px] translate-y-[10px] rounded-lg border border-primary-400/30 pointer-events-none hidden md:block" />
            {/* ── BLEED: bold accent rail ── */}
            <div className="absolute left-0 -top-3 -bottom-3 w-[5px] rounded-full bg-primary-400 z-20 pointer-events-none" />

            <div className="relative bg-primary-500 rounded-lg overflow-hidden h-full flex flex-col pl-[5px]">
            {/* Faint white rail texture */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.06]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.05]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 26px)',
              }}
            />

            <div className="relative z-10 p-6 md:p-8 lg:p-9 flex-1 flex flex-col">
            {/* ── Header: label + number synced style ── */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="block font-heading font-black text-[24px] sm:text-[32px] md:text-[38px] leading-none text-white tracking-tight">
                  成果视频
                </span>
                <span className="hidden sm:block text-[10px] text-white/40 font-medium tracking-[0.18em] uppercase">Achievement Video</span>
              </div>
              <span className="block font-heading font-black text-[48px] sm:text-[56px] md:text-[72px] lg:text-[88px] leading-none text-white/12 select-none pointer-events-none">
                02
              </span>
            </div>

            <div>
              <VideoPlayer poster={videoPoster} videoUrl={videoUrl} />
            </div>
            </div>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
}